import { toastController } from '@/controllers/toastController'
import { api } from '@/helpers/api'
import { user } from '@/helpers/api/user'
import { commonUtils } from '@/helpers/commonUtils'
import glossaryHelper, { GlossaryHelperTypes } from '@/helpers/glossaryHelper'
import { indexDbHelper } from '@/helpers/indexDbHelper'
import { throttle } from 'lodash'
import { userService } from '../userService'
import { TranslateServiceConsts as Consts, TranslateServiceConsts } from './consts'
import { TranslateServiceType as Type } from './serviceTypes'

type Locale = Type.Locale
type Paragraph = Type.Paragraph
type TranslateResult = Type.TranslateResult
type LocaleWithAutoDetect = Type.LocaleWithAutoDetect
type TriggerType = PostDataTypes.UseType

// 翻译服务超时时间常量（毫秒）
export const FREE_TRANSLATE_TIMEOUT = 5000 // 免费翻译服务超时时间：5秒
export const PREMIUM_TRANSLATE_TIMEOUT = 5000 // 高级翻译服务超时时间：5秒

interface ParagraphGroup {
  merged: boolean
  mergedText: string
  from: LocaleWithAutoDetect
  to: Locale
  paragraphs: Array<Paragraph>
  textType: 'html' | 'plain'
}

class TranslateService {
  protected storagePrefix = ''
  private localeList: Type.LocaleList = []

  private updateUsageInfoThrottleCal = throttle(
    () => {
      userService.updateUserInfo()
    },
    5000,
    { leading: true }
  )

  private compareTranslateController: AbortController | null = null
  private selectCompareTranslateController: AbortController | null = null

  /**
   * 带超时的fetch请求
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        throw new Error('REQUEST_TIMEOUT')
      }
      throw error
    }
  }

  /**
   * 处理翻译服务超时错误
   */
  private handleTranslateTimeout(translateEngine: 'microsoft' | 'google' | 'deepl') {
    if (translateEngine === 'microsoft' || translateEngine === 'google') {
      toastController.error('翻译失败，当前翻译服务不稳定，可切换其他翻译服务试试', 5)
    } else if (translateEngine === 'deepl') {
      toastController.error('翻译失败，请稍后重试或联系客服', 5)
    }
  }

  // 网页对照翻译、PDF 翻译、悬浮翻译、字幕翻译
  public async translate(
    {
      paragraphs,
      taskUid,
      originTitle,
      originUrl,
      triggerType,
      detectLang,
      shareHashId,
      fileId,
      context,
      translateEngine,
    }: {
      paragraphs: Array<Paragraph>
      taskUid: string
      originUrl: string
      originTitle: string
      triggerType: TriggerType
      detectLang?: Type.Locale
      shareHashId?: string
      fileId: number
      context?: string
      translateEngine?: 'microsoft' | 'google' | 'deepl'
    },
    callback: (results: Array<TranslateResult>) => void
  ): Promise<void> {
    if (!paragraphs || paragraphs.length <= 0) {
      callback([])
      return
    }
    // deepl 翻译引擎需要标点符号后面加空格
    paragraphs.forEach((p) => {
      p.text = this.addWhiteSpace(p.text, p.links)
    })
    const paragraphGroups = this.mergeParagraph(paragraphs)
    await this.translateParagraphGroups({
      originTitle,
      originUrl,
      paragraphGroups,
      taskUid,
      triggerType,
      detectLang,
      shareHashId,
      fileId,
      context,
      translateEngine,
      callback,
    })
  }

  public async getLocaleList(): Promise<Type.LocaleList> {
    if (this.localeList.length === 0) {
      await this.initLocaleList()
    }
    return this.sortLocale(this.localeList)
  }

  public async init() {
    this.initLocaleList()
    this.removeOldDatabase()
  }

  private removeOldDatabase() {
    try {
      if (window.indexedDB) {
        window.indexedDB.deleteDatabase('translatedTextCacheMap')
        window.indexedDB.deleteDatabase('translatedImageCacheMap')
      }
    } catch (error) {
      console.log('删除老数据库失败:translatedTextCacheMap,translatedImageCacheMap', error)
    }
  }

  private async initLocaleList() {
    let allLocales: {
      [key: string]: { name: string; nativeName: string; chineseName?: string }
    } = {}

    try {
      const { language_list } = await user.getLanguageList()
      const keys = Object.keys(language_list)
      if (keys && keys.length > 0) {
        allLocales = language_list
      }
    } catch (error) {
      allLocales = Consts.ALL_LOCALES
    }
    this.localeList = []
    Object.keys(allLocales).forEach((key) => {
      const item = allLocales[key]
      this.localeList.push({
        locale: key,
        name: item['name'],
        localName: `${item['chineseName'] ? item['chineseName'] + ' ' : ''}${item['nativeName']}`,
        chineseName: item.chineseName || item.nativeName,
      })
    })
  }

  private sortLocale(_languages: Type.LocaleList): Type.LocaleList {
    const defaultLocaleSortList = [
      'zh-Hans',
      'zh-Hant',
      'en',
      'ja',
      'ko',
      'es',
      'de',
      'fr',
      'fr-CA',
      'pt',
      'pt-PT',
      'ru',
    ]
    const languages = [..._languages]
    const sortLanguages: Type.LocaleList = []
    defaultLocaleSortList.forEach((locale) => {
      const targetLocale = languages.find((l) => l.locale === locale)
      const targetLocaleIndex = languages.findIndex((l) => l.locale === locale)
      if (targetLocale) {
        sortLanguages.push(targetLocale)
        languages.splice(targetLocaleIndex, 1)
      }
    })
    return sortLanguages.concat(languages)
  }

  private async translateByXunFeiTranslate({
    options,
    triggerType,
    callback,
  }: {
    options: PostDataTypes.TranslateOptions
    triggerType: TriggerType
    callback: (err: string | null, results: Array<ServerDataTypes.AiTranslateResult>) => void
  }) {
    const normalizedOptions = this.normalizeOptionsLocale(options)
    try {
      // 使用高级翻译服务超时时间
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), PREMIUM_TRANSLATE_TIMEOUT)
      })

      const translatePromise = api.pdf.translate({
        ...normalizedOptions,
        useType: triggerType,
      })

      const { respContentList } = await Promise.race([translatePromise, timeoutPromise])

      callback(
        null,
        respContentList.map((data, index) => {
          return {
            data,
            index,
          }
        })
      )
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // api.recordError(`xunfei_${commonUtils.formatErrorMsg(error)}`, {
      //   ...normalizedOptions,
      //   useType: triggerType,
      // })

      // 检查是否为超时错误
      if (error.message === 'REQUEST_TIMEOUT') {
        this.handleTranslateTimeout('deepl')
        callback('翻译超时，请稍后重试', [])
        return
      }

      if (error.statusCode === 400 && error.statusMessage === '找不到用户凭据') {
        userService.updateUserInfo()
      }
      callback(commonUtils.formatErrorMsg(error), [])
    }
  }

  private async translateByAiTranslate({
    options,
    triggerType,
    callback,
  }: {
    options: PostDataTypes.TranslateOptions
    triggerType: TriggerType
    callback: (err: string | null, results: Array<ServerDataTypes.AiTranslateResult>) => void
  }) {
    const normalizedOptions = this.normalizeOptionsLocale(options)
    try {
      // 使用高级翻译服务超时时间
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), PREMIUM_TRANSLATE_TIMEOUT)
      })

      const aiTranslatePromise = api.pdf.aiTranslate({
        ...normalizedOptions,
        useType: triggerType,
      })

      const { taskId } = await Promise.race([aiTranslatePromise, timeoutPromise])

      console.time('ai_translate ')
      await this.fetchAiTranslateResult({
        taskId,
        options: normalizedOptions,
        callback,
      })
      console.timeEnd('ai_translate')
    } catch (error) {
      // api.recordError(`ai_${commonUtils.formatErrorMsg(error)}`, {
      //   ...normalizedOptions,
      //   useType: triggerType,
      //   translateType: 'ai',
      // })

      // 检查是否为超时错误
      if (error.message === 'REQUEST_TIMEOUT') {
        this.handleTranslateTimeout('deepl')
        callback('翻译超时，请稍后重试', [])
        return
      }

      callback(commonUtils.formatErrorMsg(error), [])
    }
  }

  private async fetchAiTranslateResult({
    taskId,
    options,
    callback,
  }: {
    taskId: number
    options: PostDataTypes.TranslateOptions
    callback: (err: string | null, results: Array<ServerDataTypes.AiTranslateResult>) => void
  }) {
    try {
      const { status, data } = await api.pdf.getAiTranslateResult(taskId)
      callback(null, data)
      // NOTE: 增加最大循环次数判断
      if (status === 'running') {
        await commonUtils.asyncDelay(1 * 1000)
        await this.fetchAiTranslateResult({ taskId, options, callback })
      }
    } catch (error) {
      // api.recordError(`ai_result_${commonUtils.formatErrorMsg(error)}`, {
      //   ...options,
      //   taskId,
      // })
      callback(commonUtils.formatErrorMsg(error), [])
    }
  }

  private async translateByFreeService({
    options,
    triggerType,
    translateEngine,
    callback,
  }: {
    options: PostDataTypes.TranslateOptions
    triggerType: TriggerType
    translateEngine: 'microsoft' | 'google'
    callback: (err: string | null, results: Array<ServerDataTypes.AiTranslateResult>) => void
  }) {
    const normalizedOptions = this.normalizeOptionsLocale(options)
    try {
      let translatedResults: Array<string> = []

      if (translateEngine === 'microsoft') {
        translatedResults = await this.translateByMicrosoftFree(normalizedOptions)
      } else if (translateEngine === 'google') {
        translatedResults = await this.translateByGoogleFree(normalizedOptions)
      }

      callback(
        null,
        translatedResults.map((data, index) => {
          return {
            data,
            index,
          }
        })
      )
    } catch (error) {
      console.error('Free translation error:', error)

      // 检查是否为超时错误
      if (error.message === 'REQUEST_TIMEOUT') {
        this.handleTranslateTimeout(translateEngine)
        callback('翻译超时，请稍后重试', [])
      } else {
        callback(commonUtils.formatErrorMsg(error), [])
      }
    }
  }

  private async translateByMicrosoftFree(
    options: PostDataTypes.TranslateOptions
  ): Promise<Array<string>> {
    const { textList, fromLang, toLang } = options
    const accessToken = await this.getMicrosoftTranslateToken()

    // 微软翻译语言代码处理
    const normalizedFromLang = fromLang === 'auto' ? '' : commonUtils.standardizeLocale(fromLang)
    const normalizedToLang = commonUtils.standardizeLocale(toLang)

    const baseUrl = 'https://api-edge.cognitive.microsofttranslator.com/translate'
    const bodyData = textList.map((text) => ({ Text: text }))
    const bodyStr = JSON.stringify(bodyData)

    // 使用免费翻译服务超时时间
    const response = await this.fetchWithTimeout(
      `${baseUrl}?from=${normalizedFromLang}&to=${normalizedToLang}&api-version=3.0`,
      {
        method: 'POST',
        headers: {
          accept: '*/*',
          authorization: `Bearer ${accessToken}`,
          'cache-control': 'no-cache',
          'content-type': 'application/json',
          pragma: 'no-cache',
          'sec-ch-ua': '"Microsoft Edge";v="113", "Chromium";v="113", "Not-A.Brand";v="24"',
          'sec-ch-ua-mobile': '?0',
          'sec-ch-ua-platform': '"Windows"',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'cross-site',
          'Referrer-Policy': 'strict-origin-when-cross-origin',
        },
        body: bodyStr,
      },
      FREE_TRANSLATE_TIMEOUT
    )

    if (!response.ok) {
      throw new Error(`Microsoft Translate API error: ${response.status}`)
    }

    const data = await response.json()
    return data.map((item: any) => item.translations[0].text)
  }

  private async translateByGoogleFree(
    options: PostDataTypes.TranslateOptions
  ): Promise<Array<string>> {
    const { textList, fromLang, toLang } = options

    // 过滤掉换行符，防止干扰批量翻译
    const filteredTextList = textList.map((text) => text.replace(/\n/g, ''))

    // 谷歌翻译语言代码处理
    const normalizedFromLang =
      fromLang === 'auto' ? 'auto' : commonUtils.standardizeLocale(fromLang)
    const normalizedToLang = commonUtils.standardizeLocale(toLang)

    const baseUrl = 'https://translate.googleapis.com/translate_a/single'
    const params = new URLSearchParams({
      client: 'gtx',
      dt: 't',
      dj: '1',
      sl: normalizedFromLang,
      tl: normalizedToLang,
      q: filteredTextList.join('\n'),
    })

    // 使用免费翻译服务超时时间
    const response = await this.fetchWithTimeout(
      `${baseUrl}?${params.toString()}`,
      {},
      FREE_TRANSLATE_TIMEOUT
    )

    if (!response.ok) {
      throw new Error(`Google Translate API error: ${response.status}`)
    }

    const data = await response.json()
    return this.parseGoogleTranslateResult(data, filteredTextList.length)
  }

  private parseGoogleTranslateResult(data: any, expectedCount: number): Array<string> {
    const { sentences, paragraphGroup } = data
    const translatedTexts = (sentences || paragraphGroup || [])
      .map((item: any) => item.trans)
      .join('')
      .split('\n')

    // 确保返回的翻译结果数量与输入一致
    while (translatedTexts.length < expectedCount) {
      translatedTexts.push('')
    }

    return translatedTexts.slice(0, expectedCount)
  }

  private async getMicrosoftTranslateToken(): Promise<string> {
    try {
      const url = 'https://edge.microsoft.com/translate/auth'

      // 使用免费翻译服务超时时间
      const response = await this.fetchWithTimeout(
        url,
        {
          headers: {
            accept: '*/*',
            'cache-control': 'no-cache',
            pragma: 'no-cache',
            'sec-ch-ua': '"Microsoft Edge";v="113", "Chromium";v="113", "Not-A.Brand";v="24"',
            'sec-ch-ua-mobile': '?0',
            'sec-ch-ua-platform': '"Windows"',
            'sec-fetch-dest': 'empty',
            'sec-fetch-mode': 'cors',
            'sec-fetch-site': 'cross-site',
            'sec-mesh-client-arch': 'x86_64',
            'sec-mesh-client-edge-channel': 'beta',
            'sec-mesh-client-edge-version': '113.0.1774.23',
            'sec-mesh-client-os': 'Windows',
            'sec-mesh-client-os-version': '10.0.19044',
            'sec-mesh-client-webview': '0',
            Referer: 'https://translator.microsoft.com/',
            'Referrer-Policy': 'strict-origin-when-cross-origin',
          },
          method: 'GET',
        },
        FREE_TRANSLATE_TIMEOUT
      )

      if (!response.ok) {
        throw new Error(`Failed to get Microsoft token: ${response.status}`)
      }

      const token = await response.text()
      return token
    } catch (error) {
      if (error.message === 'REQUEST_TIMEOUT') {
        throw error // 保持超时错误的传递
      }
      throw new Error('Failed to authenticate with Microsoft Translate service')
    }
  }

  private mergeParagraph(paragraphs: Array<Paragraph>): Array<ParagraphGroup> {
    const paragraphGroups: Array<ParagraphGroup> = []
    // 限制详见 https://learn.microsoft.com/zh-cn/azure/cognitive-services/Translator/service-limits#character-and-array-limits-per-request
    // 一段 paragraphGroup 最多包含 40 * 1000 个字符
    const maxMergedTextLength = 40 * 1000
    // 一段 paragraphGroup 最多包含 100 个 payloads
    const maxParagraphCount = 1000
    const pushNewParagraphGroup = (paragraph: Paragraph) => {
      paragraphGroups.push({
        merged: false,
        mergedText: paragraph.text,
        from: paragraph.from,
        to: paragraph.to,
        paragraphs: [paragraph],
        textType: paragraph.htmlStr ? 'html' : 'plain',
      })
    }
    paragraphs.forEach((paragraph) => {
      const matchedGroup = this.findMatchedParagraphGroup(paragraph, paragraphGroups)
      if (matchedGroup) {
        if (
          matchedGroup.paragraphs.length < maxParagraphCount &&
          matchedGroup.mergedText.length + paragraph.text.length < maxMergedTextLength
        ) {
          matchedGroup.paragraphs.push(paragraph)
          matchedGroup.mergedText += paragraph.text
        } else {
          matchedGroup.merged = true
          pushNewParagraphGroup(paragraph)
        }
      } else {
        pushNewParagraphGroup(paragraph)
      }
    })
    return paragraphGroups
  }

  private findMatchedParagraphGroup(
    paragraph: Paragraph,
    paragraphGroups: Array<ParagraphGroup>
  ): ParagraphGroup | undefined {
    return paragraphGroups.find(({ merged, from, to, textType }) => {
      const paragraphTextType = paragraph.htmlStr ? 'html' : 'plain'
      const textTypeIsMatched = paragraphTextType === textType
      return !merged && from === paragraph.from && to === paragraph.to && textTypeIsMatched
    })
  }

  private getParagraphCacheKey(p: Paragraph): string {
    const { to, from, text } = p
    const md5 = commonUtils.md5(`$ai-${to}-${from}-${text}`)
    return md5.substring(8, 24)
  }

  private async translateParagraphGroup({
    pageTitle,
    pageUrl,
    paragraphGroup,
    taskUid,
    triggerType,
    detectLang,
    shareHashId,
    fileId,
    context,
    callback,
    translateEngine,
  }: {
    paragraphGroup: ParagraphGroup
    taskUid: string
    pageTitle: string
    pageUrl: string
    triggerType: TriggerType
    detectLang?: Type.Locale
    shareHashId?: string
    fileId: number
    context?: string
    translateEngine?: 'microsoft' | 'google' | 'deepl'
    callback: (results: Array<TranslateResult>) => void
  }): Promise<void> {
    const { from, textType, to, paragraphs } = paragraphGroup
    const options: PostDataTypes.TranslateOptions = {
      fromLang: from,
      taskUid,
      textList: paragraphs.map((p) => {
        return p.htmlStr || p.text
      }),
      toLang: to,
      textType,
      pageUrl,
      pageTitle,
      translateType: 'ai',
      detectLang,
      shareHashId,
      fileId,
      context,
    }
    await this.tryTranslateByServer(options, paragraphs, triggerType, callback, translateEngine)
  }

  private async tryGetCachedParagraphs(paragraphs: Array<Paragraph>): Promise<{
    needServiceTranslateParagraphs: Array<Paragraph>
    cachedResults: Array<TranslateResult>
  }> {
    const needServiceTranslateParagraphs: Array<Paragraph> = []
    const cachedResults: Array<TranslateResult> = []

    // 优化1: 创建缓存键到段落的映射，避免重复计算和O(n²)查找
    const cacheKeyToParagraph = new Map<string, Paragraph>()
    const cacheKeys: string[] = []

    paragraphs.forEach((p) => {
      const cacheKey = this.getParagraphCacheKey(p)
      cacheKeyToParagraph.set(cacheKey, p)
      cacheKeys.push(cacheKey)
    })

    // 批量获取缓存数据
    const cacheData = await indexDbHelper.batchGetTextByKeys(cacheKeys)

    // 优化2: 创建已缓存的键集合，提高查找效率
    const cachedKeySet = new Set(cacheData.map((item) => item.cacheKey))

    // 优化3: 使用Map进行O(1)查找，构建结果
    cacheData.forEach((item) => {
      const p = cacheKeyToParagraph.get(item.cacheKey)
      if (p) {
        cachedResults.push({
          ...p,
          result: item.translatedText,
          detectedLang: p.from,
          error: null,
        })
      }
    })

    // 优化4: 找出未缓存的段落，使用Set进行O(1)查找
    paragraphs.forEach((p) => {
      const cacheKey = this.getParagraphCacheKey(p)
      if (!cachedKeySet.has(cacheKey)) {
        needServiceTranslateParagraphs.push(p)
      }
    })

    return {
      needServiceTranslateParagraphs,
      cachedResults,
    }
  }

  private async translateParagraphGroups({
    paragraphGroups,
    originTitle,
    originUrl,
    taskUid,
    triggerType,
    detectLang,
    shareHashId,
    callback,
    fileId,
    context,
    translateEngine,
  }: {
    paragraphGroups: Array<ParagraphGroup>
    taskUid: string
    originUrl: string
    originTitle: string
    triggerType: TriggerType
    detectLang?: Type.Locale
    shareHashId?: string
    fileId: number
    context?: string
    translateEngine?: 'microsoft' | 'google' | 'deepl'
    callback: (results: Array<TranslateResult>) => void
  }): Promise<void> {
    await Promise.all(
      paragraphGroups.map(async (s) => {
        return this.translateParagraphGroup({
          pageTitle: originTitle,
          pageUrl: originUrl,
          paragraphGroup: s,
          taskUid,
          triggerType,
          detectLang,
          shareHashId,
          fileId,
          context,
          translateEngine,
          callback,
        })
      })
    )
  }

  private async tryTranslateByServer(
    options: PostDataTypes.TranslateOptions,
    paragraphs: Paragraph[],
    triggerType: TriggerType,
    callback: (results: Array<TranslateResult>) => void,
    translateEngine?: 'microsoft' | 'google' | 'deepl'
  ): Promise<void> {
    const { fromLang } = options
    const results: Array<TranslateResult> = []
    const { cachedResults, needServiceTranslateParagraphs } = await this.tryGetCachedParagraphs(
      paragraphs
    )
    // 获取缓存数据
    callback(cachedResults)
    if (needServiceTranslateParagraphs.length === 0) {
      callback([])
      return
    }
    const translatedParagraphs: Array<TranslateResult> =
      needServiceTranslateParagraphs as Array<TranslateResult>

    await this.translateByServer({
      options: {
        ...options,
        textList: needServiceTranslateParagraphs.map((p) => {
          return p.htmlStr || p.text
        }),
      },
      triggerType,
      translateEngine,
      callback: (err, data) => {
        if (err) {
          const errMsg = commonUtils.formatErrorMsg(err) || '翻译失败，请稍后重试或联系客服'
          results.push(
            ...translatedParagraphs
              .filter((p) => !p.result)
              .map((p) => {
                return {
                  ...p,
                  result: '',
                  detectedLang: fromLang,
                  error: errMsg,
                }
              })
          )
          callback(results)
        } else {
          const cacheList: Array<{ p: Paragraph; cacheData: string }> = []
          results.push(
            ...data.map(({ data, index }) => {
              const p = translatedParagraphs[index]
              cacheList.push({
                p,
                cacheData: data,
              })
              return {
                ...p,
                result: data,
                detectedLang: fromLang,
                error: null,
              }
            })
          )
          // 更新缓存数据
          this.setTranslateToCache(cacheList)
          callback(results)
        }
      },
    })
  }

  private async translateByServer({
    options,
    triggerType,
    callback,
    translateEngine,
  }: {
    options: PostDataTypes.TranslateOptions
    triggerType: TriggerType
    translateEngine?: 'microsoft' | 'google' | 'deepl'
    callback: (err: string | null, results: Array<ServerDataTypes.AiTranslateResult>) => void
  }) {
    // 如果指定了免费翻译引擎，使用免费翻译
    if (translateEngine === 'microsoft' || translateEngine === 'google') {
      await this.translateByFreeService({
        options,
        triggerType,
        translateEngine,
        callback,
      })
      return
    }

    // NOTE：默认调用机器翻译接口（DeepL等付费服务）
    const translateType: 'xunfei' | 'ai' = 'xunfei' as unknown as 'xunfei' | 'ai'
    if (translateType === 'xunfei') {
      await this.translateByXunFeiTranslate({
        options,
        triggerType,
        callback,
      })
    }
    if (translateType === 'ai') {
      await this.translateByAiTranslate({
        options,
        triggerType,
        callback,
      })
    }
  }

  private normalizeOptionsLocale(
    options: PostDataTypes.TranslateOptions
  ): PostDataTypes.TranslateOptions {
    const normalizedOptions = { ...options }
    normalizedOptions.fromLang = commonUtils.standardizeLocale(normalizedOptions.fromLang)
    if (normalizedOptions.fromLang === Consts.OLD_AUTO_DETECT_LANG) {
      normalizedOptions.fromLang = TranslateServiceConsts.AUTO_DETECT_LANG
    }
    normalizedOptions.fromLang = commonUtils.standardizeLocale(normalizedOptions.fromLang as Locale)
    normalizedOptions.toLang = commonUtils.standardizeLocale(normalizedOptions.toLang as Locale)
    return {
      ...normalizedOptions,
    }
  }

  private async getCachedTranslate(p: Paragraph) {
    const cacheData = await indexDbHelper.getTextByKey(this.getParagraphCacheKey(p))
    return cacheData?.translatedText
  }

  private async setTranslateToCache(list: Array<{ p: Paragraph; cacheData: string }>) {
    const batchData = list.map(({ p, cacheData }) => ({
      cacheKey: this.getParagraphCacheKey(p),
      translatedText: cacheData,
      timestamp: Date.now(),
    }))
    await indexDbHelper.batchSetText(batchData)
  }

  private async setImageTranslateToCache(list: Array<{ key: string; cacheData: string }>) {
    const batchData = list.map(({ key, cacheData }) => ({
      cacheKey: key,
      imgData: cacheData,
      timestamp: Date.now(),
    }))
    await indexDbHelper.batchSetImg(batchData)
  }

  private addWhiteSpace(text: string, links: string[] = []): string {
    if (!links.length) {
      text = text.replace(/([.,])/g, '$1 ')
    } else {
      text = text.replace(/([,])/g, '$1 ')
    }
    return text
  }

  public async translateImage(
    params: {
      base64: string
      fromLang: string
      toLang: string
      module: PostDataTypes.FileUploadModule
      fileId?: number
    },
    callback: (errMsg: string | null, imageResult: string) => void
  ) {
    try {
      const { base64, fromLang, toLang, module } = params
      let fileId = params.fileId

      const cacheKey = commonUtils.md5(base64 + toLang + fromLang)

      const cacheData = await indexDbHelper.getImgByKey(cacheKey)
      // console.log(cacheData, this.translatedImageCacheMap)
      if (cacheData) {
        callback(null, cacheData.imgData)
        return
      }

      if (!fileId) {
        const metaDataAndBase64 = base64.split(',')
        const metaData = metaDataAndBase64[0]
        const contentType = metaData.split(';')[0].replace('data:', '')

        const base64ToFile = (base64String: string, fileName: string, contentType: string) => {
          return fetch(base64String)
            .then((res) => res.blob())
            .then((blob) => new File([blob], fileName, { type: contentType }))
        }

        const fileName = commonUtils.md5(base64)
        const file = await base64ToFile(base64, fileName, contentType)

        const {
          data: { fileId: _fileId, signUrl },
        } = await api.pdf.getUploadSignUrlV2({
          contentLength: file.size,
          filename: file.name,
          module,
        })

        // 上传图片到CORS
        await fetch(signUrl, {
          method: 'PUT',
          body: file,
        })
        fileId = _fileId
      }
      // 翻译
      const { translatePicImage } = await api.pdf.imageTranslate({
        fileId: fileId!,
        fromLang,
        toLang,
        useType: module === 'translate_pdf' ? 'pdf_pic_translate' : 'pic_translate',
      })
      callback(null, translatePicImage)
      // 设置到本地缓存
      this.setImageTranslateToCache([{ key: cacheKey, cacheData: translatePicImage }])
    } catch (error) {
      callback(commonUtils.formatErrorMsg(error), '')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // api.recordError(`youdao_${commonUtils.formatErrorMsg(error)}`, { useType: 'image' } as any)
    }

    this.updateUsageInfoThrottleCal()
  }

  public async compareTranslate(
    textList: {
      text: string
      model: ServerDataTypes.Model
      fromLang: string
      toLang: string
      preprocessResult?: GlossaryHelperTypes.PreprocessResult
    }[],
    callback: (results: {
      index: number
      text: string
      model: ServerDataTypes.Model
      result: string
      errMsg?: string
    }) => void,
    useType: 'pdf_compare' | 'text_compare'
  ) {
    if (this.compareTranslateController) {
      this.compareTranslateController.abort()
    }

    const glossaryData = await indexDbHelper.batchGetGlossaryData({
      fromLang: textList[0].fromLang,
      toLang: textList[0].toLang,
    })

    textList.forEach((item) => {
      const preprocessResult = glossaryHelper.preprocessText(item.text, glossaryData)
      item.text = preprocessResult.processedText
      item.preprocessResult = preprocessResult
    })

    this.compareTranslateController = new AbortController()
    const allTasks = textList.map(async (item, index) => {
      try {
        const res = await api.onlineTranslate.textTranslate(
          {
            text: item.text,
            fromLang: item.fromLang,
            toLang: item.toLang,
            useType,
            translateModel: item.model.key,
          },
          this.compareTranslateController!
        )

        if (this.compareTranslateController?.signal.aborted) {
          return
        }

        if (item.preprocessResult) {
          res.content = glossaryHelper.postprocessText(res.content, item.preprocessResult).finalText
        }
        callback({
          index,
          text: item.text,
          model: item.model,
          result: res.content,
        })
      } catch (error) {
        // 检查是否是 AbortError，如果是则直接返回，不调用 callback
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        // 双重检查：也检查 signal 状态
        if (this.compareTranslateController?.signal.aborted) {
          return
        }
        callback({
          index,
          text: item.text,
          model: item.model,
          result: '',
          errMsg: `翻译失败，请稍后重试或联系客服[${commonUtils.formatErrorMsg(error)}]`,
        })
      }
    })
    try {
      await Promise.all(allTasks)
      this.updateUsageInfoThrottleCal()
    } catch (error) {
      if (!this.compareTranslateController?.signal.aborted) {
        console.error('compareTranslate error', error)
      }
    } finally {
      this.compareTranslateController = null
    }
  }

  public async stopCompareTranslate() {
    if (this.compareTranslateController) {
      this.compareTranslateController.abort()
      this.compareTranslateController = null
    }
  }

  // 获取词典数据
  public async getDictTranslateData(
    text: string,
    fromLang: Type.Locale,
    toLang: Type.Locale,
    detectLang?: Type.Locale
  ): Promise<{
    bingDict: Type.BingDictResult | null
    scribeResult?: ServerDataTypes.ScribeTranslateResult
  }> {
    // 创建临时的 Paragraph 对象用于调用 searchBingDict
    const tempParagraph: Type.Paragraph = {
      id: commonUtils.genId(),
      text,
      htmlStr: text,
      from: fromLang,
      to: toLang,
      detectedLang: detectLang || fromLang,
    }

    // 获取必应词典数据
    const bingDictData = await this.searchBingDict({
      to: toLang,
      paragraph: tempParagraph,
    })

    // 调用 scribeTranslate API 获取详细的翻译信息
    try {
      const {
        data: { data, wordType, translateRecordId },
      } = await api.onlineTranslate.scribeTranslate({
        fromLang,
        toLang,
        text,
        taskUid: commonUtils.genId(),
        useType: 'real_time',
        detectLang: detectLang || fromLang,
        bingDict: bingDictData,
      })

      if (data.phonetic) {
        data.phonetics = [
          {
            phonetic: `美 [${data.phonetic}]`,
            type: 'us',
          },
        ]
      }
      if (!data.phonetics) {
        data.phonetics = []
      }

      return {
        bingDict: bingDictData,
        scribeResult: {
          data,
          wordType,
          translateRecordId,
          errMsg: null,
        },
      }
    } catch (error) {
      console.error('调用 scribeTranslate 失败:', error)
      // 如果 scribeTranslate 失败，只返回必应词典数据
      return {
        bingDict: bingDictData,
      }
    }
  }
  /**
   * 调用 Bing 词典接口
   * 仅支持中英互译
   */
  private async searchBingDict({
    to,
    paragraph,
  }: {
    to: string
    paragraph: Paragraph
  }): Promise<Type.BingDictResult | null> {
    // 仅支持译中/译英
    if (to !== 'en' && to !== 'zh-Hans') {
      return Promise.resolve(null)
    }
    const { text, detectLang } = paragraph as any
    // 仅支持长度小于 4 的中文词语调用词典（中文词语多为单音词和双音词，例如吃、老师
    // 长度超出 4 则不认为是词语
    if (detectLang === 'zh-Hans' && text.length > 4) {
      return Promise.resolve(null)
    }
    // 仅支持单个单词调用词典
    if (detectLang === 'en' && text.split(' ').length > 1) {
      return Promise.resolve(null)
    }
    const abort = new AbortController()
    setTimeout(() => {
      abort.abort()
    }, 2000)
    try {
      const res = await api.onlineTranslate.getDictSearch({
        q: text,
        cc: 'cn',
      })
      const result = await res.data
      // 用正则匹配出翻译结果
      // const reg = /<meta name="description" content="必应词典为您提供(.+?)"/
      // const match = html.match(reg)
      // const result = match?.[1]
      console.log('result-SEARCH', result)
      if (result) {
        // 翻译结果中，音标间以「，」分隔，音标与翻译结果间以「，」分隔，翻译结果间以「； 」分隔
        const resultList = result.split('，')
        // 第一个元素为 bing dict 的固定提示语，故移除
        resultList.shift()
        let phonetics: Type.BingDictPhonetic[] = []
        let translates: Array<string> = []
        // 包含音标的处理
        if (result.indexOf('英') > -1 || result.indexOf('美') > -1 || result.indexOf('拼音') > -1) {
          // 最后一个元素为翻译结果
          const translateResultStr = resultList.pop()!
          // 剩余元素为音标结果
          const phoneticResult = resultList
          phonetics = phoneticResult.map((r: string) => {
            let type: Type.BingDictPhoneticType = 'us'
            if (r.indexOf('英') > -1) {
              type = 'uk'
            } else if (r.indexOf('拼音') > -1) {
              type = 'py'
            }
            return {
              type,
              phonetic: r,
            }
          })
          // 多种词形由「； 」做分隔
          translates = translateResultStr.split('； ').filter((r: string) => {
            return !!r
          })
        } else {
          // 不包含音标的处理
          translates = resultList[0].split('； ').filter((r: string) => {
            return !!r
          })
        }
        if (translates.length) {
          return Promise.resolve({
            transList: translates,
            phonetics,
          })
        }
      }
      return Promise.resolve(null)
    } catch (error) {
      return Promise.resolve(null)
    }
  }
}

export const translateService = new TranslateService()
